import { Box, Group, createStyles } from '@mantine/core';
import type {
	ActivityLogEventBlock,
	ActivityLogOut,
	LiteUserOut,
} from '@repo/api-codegen';
import { useApiListApiKeysV1, useGetAccessRequest } from '@repo/api-codegen';
import { UserAvatar } from '@repo/common/components';
import { EventBlock } from '@repo/common/components/ActivityLog/EventBlock';
import type { EntityType } from '@repo/common/enums/entityType';
import { getEntityTypeIcon } from '@repo/common/utils/entityDisplayUtils';
import { Button, Icon, Text } from '@repo/foundations';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useWorkspace } from '../../api';
import { useIntegrationPrefetched } from '../../api/hooks/integration';
import { useIncident } from '../../api/hooks/monitoring';
import { useSecodaEntity } from '../../api/hooks/secodaEntity';
import { buildResourceUrl } from '../../utils/navigationUtils';
import IntegrationLogo from '../IntegrationLogo';
import { SecodaEntityIcon } from '../SecodaEntity';

const useStyles = createStyles((theme) => ({
	group: {
		padding: `0 ${theme.spacing.sm}`,
	},
}));

export function ActivityLogNoResourceRender() {
	const { classes } = useStyles();
	return (
		<Group spacing="2xs" noWrap className={classes.group}>
			<Text size="sm" color="text/primary/default">
				None
			</Text>
		</Group>
	);
}

function ResourceRender({
	icon,
	displayName,
	url,
}: {
	icon: ReactNode;
	displayName: ReactNode;
	url?: string;
}) {
	const navigate = useNavigate();
	const { classes } = useStyles();
	const children = (
		<Group spacing="2xs" noWrap>
			{icon}
			<Text size="sm" color="text/primary/default">
				{displayName}
			</Text>
		</Group>
	);

	if (url) {
		return (
			<Button variant="tertiary" onClick={() => navigate(url)}>
				{children}
			</Button>
		);
	} else {
		return (
			<Group spacing="2xs" noWrap className={classes.group}>
				{children}
			</Group>
		);
	}
}

function IntegrationRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			url={
				record.resource_metadata?.id
					? `/integrations/${record.resource_metadata.id}`
					: undefined
			}
			icon={
				<Box p="4xs">
					<IntegrationLogo
						integrationId={record.resource_metadata?.id}
						integrationType={record.resource_metadata?.type ?? undefined}
						width={16}
						height={16}
					/>
				</Box>
			}
			displayName={record.resource_metadata?.name}
		/>
	);
}

function ApiKeyRender({ record }: { record: ActivityLogOut }) {
	const { data: apiKeys } = useApiListApiKeysV1(
		{},
		{
			enabled: record.resource_type === 'API_KEY' && !!record.resource_id,
		}
	);
	const apiKey = apiKeys?.results.find((key) => key.id === record.resource_id);

	if (!apiKey) {
		return <ActivityLogNoResourceRender />;
	}

	return (
		<ResourceRender
			icon={<Icon name="key" color="icon/primary/default" />}
			displayName={apiKey.key}
		/>
	);
}

function AutomationRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			url={`/automation/${record.resource_id}`}
			icon={<Icon name="bolt" color="icon/primary/default" />}
			displayName={record.resource_metadata?.name ?? 'Untitled'}
		/>
	);
}

function CommentRender({ record }: { record: ActivityLogOut }) {
	const { data: dataAccessRequestForComment } = useGetAccessRequest(
		{
			pathParams: {
				requestId: record.related_resource_ids[0] ?? '',
			},
		},
		{
			enabled:
				record.resource_type === 'COMMENT' && !!record.related_resource_ids[0],
		}
	);

	const { data: incidentForComment } = useIncident({
		id: record.related_resource_ids[0] ?? '',
		options: {
			enabled:
				record.resource_type === 'COMMENT' && !!record.related_resource_ids[0],
		},
	});

	const { data: secodaEntityForComment } = useSecodaEntity({
		id: record.related_resource_ids[0] ?? '',
		options: {
			enabled:
				record.resource_type === 'COMMENT' && !!record.related_resource_ids[0],
		},
	});

	let displayName = 'Comment';
	let icon: ReactNode | null = null;
	let url: string | undefined = undefined;

	if (dataAccessRequestForComment) {
		displayName = `${dataAccessRequestForComment.integration_name} access`;
		icon = <Icon name="squareRoundedCheck" color="icon/primary/default" />;
		url = `/requests/${dataAccessRequestForComment.id}`;
	}

	if (incidentForComment) {
		displayName = incidentForComment.name ?? 'Untitled';
		icon = <Icon name="activity" color="icon/primary/default" />;
		url = `/incident/${incidentForComment.id}`;
	}

	if (secodaEntityForComment) {
		displayName = secodaEntityForComment.title ?? 'Untitled';
		icon = getEntityTypeIcon(secodaEntityForComment.entity_type);
		url = buildResourceUrl(secodaEntityForComment);
	}

	return <ResourceRender icon={icon} displayName={displayName} url={url} />;
}

function CustomPropertyRender({ record }: { record: ActivityLogOut }) {
	const displayName = record.resource_metadata?.name ?? 'Custom property';
	const icon = record.resource_metadata?.type ? (
		getEntityTypeIcon(record.resource_metadata.type as EntityType)
	) : (
		<Icon name="tag" color="icon/primary/default" />
	);

	return <ResourceRender icon={icon} displayName={displayName} />;
}

function DataAccessRequestRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			url={`/requests/${record.resource_metadata?.id}`}
			icon={<Icon name="squareRoundedCheck" color="icon/primary/default" />}
			displayName={record.resource_metadata?.name ?? 'Access request'}
		/>
	);
}

function IncidentRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			url={
				record.resource_metadata?.id
					? `/incident/${record.resource_metadata?.id}`
					: undefined
			}
			icon={<Icon name="activity" color="icon/primary/default" />}
			displayName={record.resource_metadata?.name ?? 'Incident'}
		/>
	);
}

function MonitorNotificationRuleRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			url={`/settings/monitoring/`}
			icon={<Icon name="activity" color="icon/primary/default" />}
			displayName={record.resource_metadata?.name ?? 'Notification Rule'}
		/>
	);
}

function MetricRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			icon={<Icon name="chartBar" color="icon/primary/default" />}
			displayName={record.resource_metadata?.name ?? 'Metric'}
		/>
	);
}

function MonitorRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			url={
				record.resource_metadata?.id
					? `/monitor/${record.resource_metadata?.id}`
					: undefined
			}
			icon={<Icon name="activity" color="icon/primary/default" />}
			displayName={record.resource_metadata?.name ?? 'Monitor'}
		/>
	);
}

function PolicyRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			icon={<Icon name="fileCheck" color="icon/primary/default" />}
			displayName={record.resource_metadata?.name ?? 'Policy'}
			url={
				record.resource_metadata?.id
					? `/policy/${record.resource_metadata?.id}`
					: undefined
			}
		/>
	);
}

function ReportRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			url={
				record.resource_metadata?.id
					? `/analytics/${record.resource_metadata?.id}`
					: undefined
			}
			icon={<Icon name="chartBar" color="icon/primary/default" />}
			displayName={record.resource_metadata?.name ?? 'Report'}
		/>
	);
}

function EntityRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			url={buildResourceUrl({
				id: record.resource_metadata?.id ?? '',
				entity_type: record.resource_metadata?.type,
			})}
			icon={
				<SecodaEntityIcon
					size={20}
					entityType={record.resource_metadata?.type as EntityType}
					integrationId={record.resource_metadata?.integration_id ?? undefined}
					icon={record.resource_metadata?.icon ?? undefined}
				/>
			}
			displayName={record.resource_metadata?.name ?? 'Resource'}
		/>
	);
}

function TagRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			icon={<Icon name="tag" color="icon/primary/default" />}
			displayName={record.resource_metadata?.name ?? 'Tag'}
		/>
	);
}

function TagGroupRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			icon={<Icon name="tag" color="icon/primary/default" />}
			displayName={record.resource_metadata?.name ?? 'Tag group'}
		/>
	);
}

function TeamRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			url="/teams"
			icon={<Icon name="users" color="icon/primary/default" />}
			displayName={record.resource_metadata?.name ?? 'Team'}
		/>
	);
}

function UserRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			url={
				record.resource_metadata?.id
					? `/user/${record.resource_metadata.id}`
					: undefined
			}
			icon={<Icon name="users" color="icon/primary/default" />}
			displayName={record.resource_metadata?.name ?? 'User'}
		/>
	);
}

function UserGroupRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			icon={<Icon name="users" color="icon/primary/default" />}
			displayName={record.resource_metadata?.name ?? 'User group'}
		/>
	);
}

function WorkspaceRender({ record }: { record: ActivityLogOut }) {
	return (
		<ResourceRender
			icon={<Icon name="building" color="icon/primary/default" />}
			displayName={record.resource_metadata?.name ?? 'Workspace'}
		/>
	);
}

export function ActivityLogResourceRender({
	record,
}: {
	record: ActivityLogOut;
}) {
	const { workspace } = useWorkspace();

	if (!record.resource_type || !record.resource_id) {
		return (
			<ResourceRender
				icon={<Icon name="building" color="icon/primary/default" />}
				displayName={workspace?.name ?? ''}
			/>
		);
	}

	switch (record.resource_type) {
		case 'API_KEY':
			return <ApiKeyRender record={record} />;
		case 'AUTOMATION':
			return <AutomationRender record={record} />;
		case 'BACKGROUND_JOB':
			return (
				<ResourceRender
					icon={<Icon name="settings" color="icon/primary/default" />}
					displayName="System"
				/>
			);
		case 'COMMENT':
			return <CommentRender record={record} />;
		case 'CUSTOM_PROPERTY':
			return <CustomPropertyRender record={record} />;
		case 'DATABUILDER_JOB':
			return (
				<ResourceRender
					icon={<Icon name="settings" color="icon/primary/default" />}
					displayName="System"
				/>
			);
		case 'DATA_ACCESS_REQUEST':
			return <DataAccessRequestRender record={record} />;
		case 'IAM_RESOURCE_POLICY':
			return (
				<ResourceRender
					icon={<Icon name="users" color="icon/primary/default" />}
					displayName="IAM resource policy"
				/>
			);
		case 'INCIDENT':
			return <IncidentRender record={record} />;
		case 'INTEGRATION':
			return <IntegrationRender record={record} />;
		case 'INVITATION_LINK':
			return (
				<ResourceRender
					icon={<Icon name="user" color="icon/primary/default" />}
					displayName="Invitation link"
				/>
			);
		case 'LINEAGE':
			return (
				<ResourceRender
					icon={<Icon name="listDetails" color="icon/primary/default" />}
					displayName="Lineage"
				/>
			);
		case 'MARKETPLACE_INTEGRATION_SPEC':
			return (
				<ResourceRender
					icon={<Icon name="plugConnected" color="icon/primary/default" />}
					displayName="Marketplace integration spec"
				/>
			);
		case 'MARKETPLACE_INTEGRATION_SPEC_VERSION':
			return (
				<ResourceRender
					icon={<Icon name="plugConnected" color="icon/primary/default" />}
					displayName="Marketplace integration spec version"
				/>
			);
		case 'METRIC':
			return <MetricRender record={record} />;
		case 'MONITOR':
			return <MonitorRender record={record} />;
		case 'MONITOR_NOTIFICATION_RULE':
			return <MonitorNotificationRuleRender record={record} />;
		case 'POLICY':
			return <PolicyRender record={record} />;
		case 'QUERY':
			return (
				<ResourceRender
					icon={<Icon name="bolt" color="icon/primary/default" />}
					displayName="Query"
				/>
			);
		case 'QUESTION':
			return (
				<ResourceRender
					icon={
						<Icon name="messageCircleQuestion" color="icon/primary/default" />
					}
					displayName={record.resource_metadata?.name ?? 'Question'}
					url={`/question/${record.resource_metadata?.id}`}
				/>
			);
		case 'QUESTION_REPLY':
			return (
				<ResourceRender
					icon={
						<Icon name="messageCircleQuestion" color="icon/primary/default" />
					}
					displayName="Question reply"
				/>
			);
		case 'REPORT':
			return <ReportRender record={record} />;
		case 'S3_OBJECT':
			return (
				<ResourceRender
					icon={<Icon name="database" color="icon/primary/default" />}
					displayName="S3 object"
				/>
			);
		case 'SECODA_ENTITY':
			return <EntityRender record={record} />;
		case 'TAG':
			return <TagRender record={record} />;
		case 'TAG_GROUP':
			return <TagGroupRender record={record} />;
		case 'TEAM':
			return <TeamRender record={record} />;
		case 'USER':
			return <UserRender record={record} />;
		case 'USER_GROUP':
			return <UserGroupRender record={record} />;
		case 'WORKSPACE':
			return <WorkspaceRender record={record} />;
		case 'WIDGET':
			return (
				<ResourceRender
					icon={<Icon name="chartBar" color="icon/primary/default" />}
					displayName="Widget"
				/>
			);
		default:
			return <ActivityLogNoResourceRender />;
	}
}

export function ActivityLogEventBlocksRender({
	blocks,
}: {
	blocks: ActivityLogEventBlock[];
}) {
	return (
		<Group spacing={0} sx={{ whiteSpace: 'pre' }}>
			{blocks.map((block, index) => (
				<EventBlock
					key={index}
					block={block}
					capitalizeFirst={index === 0}
					last={index === blocks.length - 1}
				/>
			))}
		</Group>
	);
}

export function ActivityLogPerformerRender({
	record,
}: {
	record: ActivityLogOut;
}) {
	const { data: integration } = useIntegrationPrefetched({
		id: record.performer_id ?? '',
	});

	if (record.performer_type === 'AUTOMATION') {
		return (
			<Group spacing="2xs" noWrap>
				<Icon name="bolt" />
				<Text size="sm" color="text/primary/default">
					Automation
				</Text>
			</Group>
		);
	}

	if (record.performer_type === 'MONITOR') {
		return (
			<Group spacing="2xs" noWrap>
				<Icon name="activity" />
				<Text size="sm" color="text/primary/default">
					Monitor
				</Text>
			</Group>
		);
	}

	if (record.performer_type === 'INTEGRATION') {
		return (
			<Group spacing="2xs" noWrap>
				<Box p="4xs">
					<IntegrationLogo
						integrationId={record.resource_metadata?.id}
						integrationType={record.resource_metadata?.type ?? undefined}
						width={16}
						height={16}
					/>
				</Box>
				<Text size="sm" color="text/primary/default">
					{record.resource_metadata?.name}
				</Text>
			</Group>
		);
	}
	if (
		['USER', 'API'].includes(record.performer_type) &&
		record.performer_metadata
	) {
		return (
			<Group spacing="2xs" noWrap>
				<UserAvatar size="xs" user={record.performer_metadata as LiteUserOut} />
				<Text size="sm" color="text/primary/default">
					{record.performer_metadata?.display_name}
					{record.performer_type === 'API' && (
						<Text
							size="sm"
							color="text/secondary/default"
							span
						>{` via API`}</Text>
					)}
				</Text>
			</Group>
		);
	}
	if (record.performer_type === 'UNKNOWN') {
		return (
			<Group spacing="2xs" noWrap>
				<Icon name="user" />
				<Text size="sm" color="text/primary/default">
					Unknown
				</Text>
			</Group>
		);
	}

	return null;
}

export function ActivityLogEventBlockRender({
	blocks,
}: {
	blocks: ActivityLogEventBlock[];
}) {
	if (!blocks) {
		return null;
	}

	return (
		<Text>
			{blocks.map((block) => {
				if (block.type === 'text' && block.style === 'normal') {
					return <Text key={block.value}>{block.value}</Text>;
				}

				if (block.type === 'text' && block.style === 'bold') {
					return (
						<Text key={block.value} weight="bold">
							{block.value}
						</Text>
					);
				}

				return null;
			})}
		</Text>
	);
}
